import {
  ESdChangeReason,
  IActionDefinitionDto,
  IApiManagement,
  IApiManagementDto,
  IScheduledJob,
  IScheduledJobsDto,
  IWorkFlow,
  IWorkFlowDto,
  LeftPanelType,
  ViewPart,
  Visibility
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { ApiManagementService } from "../../../services/api-management-service";
import { DatasourceService } from "../../../services/datasource-service";
import { ScheduledJobsService } from "../../../services/scheduled-jobs-service";
import { WorkFlowService } from "../../../services/workflow-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../shared/pure-sd";
import { getSdInstance } from "../../../shared/utility/designer-context";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import BluetoothSvg from "../../../svgs/bluetooth.svg";
import ApiManagementPassiveSvg from "../../../svgs/dashboard-states-icon/api-management-passive.svg";
import ScheduledJobsPassiveSvg from "../../../svgs/dashboard-states-icon/scheduled-jobs-passive.svg";
import WorkflowPassiveSvg from "../../../svgs/dashboard-states-icon/workflow-passive.svg";
import { DeleteModal } from "../../common/delete-modal";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaOverflowText from "../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";

export interface IRightPanelItemNameComponentState {
  itemToDelete: IWorkFlowDto | IApiManagement | IScheduledJob | IActionDefinitionDto | null;
  itemToRename: IWorkFlowDto | IApiManagement | IScheduledJob | IActionDefinitionDto | null;
  isRenaming: boolean;
  renamingId: string;
  isRenameLoading: boolean;
}

export interface IRightPanelItemNameComponentProps {}

class RightPanelItemNameComponent extends PureSdComponent<
  IRightPanelItemNameComponentProps & IWithSDProps & IWithContextProp,
  IRightPanelItemNameComponentState
> {
  nameInput: HTMLInputElement | null = null;

  constructor(props) {
    super(props);

    const sVar = {
      itemToDelete: null,
      itemToRename: null,
      isRenaming: false,
      renamingId: "",
      isRenameLoading: false
    };
    this.state = sVar;
  }

  async deleteWorkFlow() {
    const wf = this.state.itemToDelete as IWorkFlowDto;
    try {
      const res: any = await WorkFlowService.Delete(wf.id);
      getSdInstance().startProcessTriggerSync.pruneWorkflow(wf.id);
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
    await this.props.sd.loadWorkFlowList(this.props.sd.project.id);
    this.props.sd.setActiveWorkFlowLeftPanel(LeftPanelType.WorkFlowList);
    this.props.sd.setCreatingWfActive(true);
    this.props.sd.clearSelectedWf();
    if (this.props.sd.lastOpenWfId && this.props.sd.lastOpenWfId === wf.id) {
      this.props.sd.clearLastOpenedWfId();
    }
  }

  async inactivateWorkFlow(wfId: string) {
    try {
      const res: any = await WorkFlowService.Inactivate(wfId);
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
    await this.props.sd.loadWorkFlowList(this.props.sd.project.id);
    this.props.sd.setActiveWorkFlowLeftPanel(LeftPanelType.WorkFlowList);
    this.props.sd.clearSelectedWf();
    this.props.sd.setCreatingWfActive(true);
  }

  async activateWorkFlow(wfId: string) {
    try {
      const res: any = await WorkFlowService.Activate(wfId);
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
    await this.props.sd.loadWorkFlowList(this.props.sd.project.id);
    this.props.sd.setSelectedWf(wfId);
    // this.props.sd.setCreatingWfActive(true);
  }

  // The dropdown menu hands its handlers rc-menu's click *info* object, not a DOM event, so
  // nothing here can stop propagation — and nothing needs to: the menu is portalled out of
  // this panel, so a click inside it never travels through the panel's own tree.
  triggerDeleteModal = (itemToDelete: IWorkFlowDto | IApiManagement | IScheduledJob | IActionDefinitionDto): void => {
    this.setState({ itemToDelete });
  };

  async inactivateSj(id: string) {
    try {
      const res: any = await ScheduledJobsService.Inactivate(id);
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
    await this.props.sd.loadScheduledJobs(this.props.sd.project.id);
  }

  async activateSj(id: string) {
    try {
      const res: any = await ScheduledJobsService.Activate(id);
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
    await this.props.sd.loadScheduledJobs(this.props.sd.project.id);
  }

  deleteSj = async () => {
    const sJob = this.state.itemToDelete as IScheduledJob;
    await ScheduledJobsService.Delete(sJob.id)
      .then((response) => {
        if (response) {
          getSdInstance().startProcessTriggerSync.pruneScheduledJob(sJob.id);
          this.props.sd.loadScheduledJobs(this.props.sd.project.id);
          if (this.props.sd.selectedSjId === sJob.id) {
            this.props.sd.setCreateSJobActive(true);
          }
          KMainFunctions.displaySuccessNotification("Scheduled Job deleted successfully");
        }
      })
      .catch((error) => {
        KMainFunctions.displayErrorNotification("An error occurred!", error);
      });
  };

  deleteApi = async () => {
    const api = this.state.itemToDelete as IApiManagement;
    await ApiManagementService.Delete(api.id)
      .then((response) => {
        if (response) {
          getSdInstance().startProcessTriggerSync.pruneApiManagement(api.id);
          this.props.sd.loadApiManagements(this.props.sd?.project?.id);
          if (this.props.sd.selectedApiId === api.id) {
            this.props.sd.setActiveApi(undefined!);
            this.props.sd.setEditingMethod(undefined);
            KMainFunctions.displaySuccessNotification("Api deleted successfully");
          }
        }
      })
      .catch((error) => {
        KMainFunctions.displayErrorNotification("An error occurred!", error);
      });
  };

  deleteBleMethod = () => {
    const method = this.state.itemToDelete as IActionDefinitionDto;
    DatasourceService.DeleteAction({ ...method, bluetoothInfo: JSON.stringify(method.bluetoothInfo) as any })
      .then((response) => {
        if (response.status === 200) {
          this.props.sd.refreshActions();
          this.props.sd.setActiveBluetoothMethod(undefined);
          this.props.sd.setEditingBluetoothMethod(undefined);
        }
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
      });
  };

  cancelRenaming = () => {
    this.setState({ isRenaming: false, renamingId: "", isRenameLoading: false });
  };

  changeWorkFlowName = async (wf: IWorkFlow | undefined, newName: string) => {
    if (!newName) {
      KMainFunctions.displayWarningNotification("Please enter a valid name.");
      return;
    }

    this.setState({ isRenameLoading: true });
    const WorkFlowNameRules = /^[A-Za-z][A-Za-z0-9 ]+$/;
    const isWorkFlowNameValid = WorkFlowNameRules.test(newName);
    if (!isWorkFlowNameValid) {
      KMainFunctions.displayWarningNotification("Process name is invalid.");
      this.setState({ isRenameLoading: false });
      this.cancelRenaming();
      return;
    }

    const isEqualToDsName = this.props.sd?.datasources?.find((ds) => ds.name.toLowerCase() === newName?.toLowerCase());
    const isEqualToScreenName = this.props.sd?.screenList?.find(
      (screen) => screen.name.toLowerCase() === newName?.toLowerCase()
    );
    if (isEqualToDsName || isEqualToScreenName) {
      this.setState({ isRenameLoading: false });
      KMainFunctions.displayWarningNotification("Process name can not be equal to the datasource name or screen name");
      return;
    }
    const isEqualToWorkflowName = this.props.sd.workFlowList.find(
      (wf) => wf.id !== this.state.renamingId && wf.name.toLowerCase() === newName?.toLowerCase()
    );

    if (isEqualToWorkflowName) {
      this.setState({ isRenameLoading: false });
      KMainFunctions.displayWarningNotification("Process name already exists");
      return;
    }

    try {
      if (newName?.length > 0 && wf) {
        const obj = {} as IWorkFlowDto;
        obj.name = newName;
        const res = await WorkFlowService.Update(wf.id, obj);
        if (res && res.status === 200) {
          await this.props.sd.loadWorkFlowList(this.props.sd.project.id);
          // this.props.sd.updateLaneName("WfName");
        }
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isRenameLoading: false });
      this.cancelRenaming();
    }
  };

  changeScheduledJobName = async (sJob: IScheduledJob | undefined, newName: string) => {
    this.setState({ isRenameLoading: true });
    if (!sJob) return;
    const sJobNameRules = /^[A-Za-z][A-Za-z0-9 ]+$/;
    const issJobNameValid = sJobNameRules.test(newName);
    const isSJobNameExist = this.props.sd.scheduledJobs.some(
      (sj) => sj.name.toLowerCase() === newName.toLowerCase() && sj.id !== sJob.id
    );

    if (!newName) {
      KMainFunctions.displayWarningNotification("Please enter a name for the scheduled job.");
      this.setState({ isRenameLoading: false });
      this.cancelRenaming();
      return;
    }
    if (!issJobNameValid) {
      KMainFunctions.displayWarningNotification("Scheduled Job name is invalid");
      this.setState({ isRenameLoading: false });
      this.cancelRenaming();
      return;
    }
    if (isSJobNameExist) {
      KMainFunctions.displayWarningNotification("Scheduled Job name already exist");
      this.setState({ isRenameLoading: false });
      this.cancelRenaming();
      return;
    }
    const obj = {
      id: sJob.id,
      name: newName,
      kProject_Id: this.props.sd.project?.id,
      json: JSON.stringify(sJob.json),
      xml: "scheduledjobs"
    } as IScheduledJobsDto;
    this.updateScheduledJob(obj);
  };

  updateScheduledJob = async (sJobObj: IScheduledJobsDto) => {
    await this.props.sd
      .saveScheduledJob(sJobObj)
      .catch((error) => {
        KMainFunctions.displayErrorNotification("An error occurred!", error);
      })
      .finally(() => {
        this.setState({ isRenameLoading: false });
        this.cancelRenaming();
        this.props.sd.cancelEditingScheduledJob();
        this.props.sd.setActiveScheduledJob(sJobObj.id);
      });
  };

  changeApiName = async (api: IApiManagement | undefined, newName: string) => {
    if (!api) return;
    this.setState({ isRenameLoading: true });
    const apiNameRules = /^[A-Za-z][A-Za-z0-9 ]+$/;
    const isApiNameValid = apiNameRules.test(newName);
    const isApiNameExist = _.find(this.props.sd.project.apiManagements, { name: newName.toLowerCase() });

    if (!newName) {
      KMainFunctions.displayWarningNotification("Please enter a name for the api.");
      this.setState({ isRenameLoading: false });
      this.cancelRenaming();
      return;
    }
    if (!isApiNameValid) {
      KMainFunctions.displayWarningNotification("Api name is invalid.");
      this.setState({ isRenameLoading: false });
      this.cancelRenaming();
      return;
    }
    if (isApiNameExist) {
      KMainFunctions.displayWarningNotification("Api name already exist.");
      this.setState({ isRenameLoading: false });
      this.cancelRenaming();
      return;
    }

    const obj = {
      id: api.id,
      name: newName,
      kProject_Id: this.props.sd.project?.id,
      json: JSON.stringify(api.json),
      xml: "api",
      dynamicInput: api.dynamicInput
    } as IApiManagementDto;
    this.updateApi(obj);
  };

  updateApi = (apiObj: IApiManagementDto) => {
    this.props.sd
      .saveApiManagements(apiObj)
      .catch((error) => {
        KMainFunctions.displayErrorNotification("An error occurred!", error);
      })
      .finally(() => {
        this.setState({ isRenameLoading: false });
        this.cancelRenaming();
      });
  };

  changeBleMethodName = (method: IActionDefinitionDto | undefined, newName: string) => {
    if (!method || !method.bluetoothInfo) return;
    this.setState({ isRenameLoading: true });
    const methodNameRules = /^[A-Za-z][A-Za-z0-9 ]+$/;
    const methodNameIsValid = methodNameRules.test(newName);
    const isMethodNameExist = _.find(this.props.sd.actionDefinitions, { name: newName.toLowerCase() });

    if (!newName) {
      KMainFunctions.displayWarningNotification("Please enter a name for the bluetooth method.");
      this.setState({ isRenameLoading: false });
      this.cancelRenaming();
      return;
    }
    if (!methodNameIsValid) {
      KMainFunctions.displayWarningNotification("Bluetooth method name is invalid.");
      this.setState({ isRenameLoading: false });
      this.cancelRenaming();
      return;
    }
    if (isMethodNameExist) {
      KMainFunctions.displayWarningNotification("Bluetooth method name already exist.");
      this.setState({ isRenameLoading: false });
      this.cancelRenaming();
      return;
    }

    const obj = { ...method, name: newName, bluetoothInfo: { ...method.bluetoothInfo, functionName: newName } };

    this.updateBleMethod(method);
  };

  updateBleMethod = (method: IActionDefinitionDto) => {
    DatasourceService.SaveAction(method)
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
      })
      .finally(() => {
        this.setState({ isRenameLoading: false });
        this.cancelRenaming();
      });
  };

  getItemName = (isWf: boolean, isSj: boolean, isApi: boolean, isBle) => {
    if (isWf) {
      return this.props.sd.selectedWf?.name;
    }
    if (isApi) {
      return this.props.sd.getSelectedApi()?.name;
    }
    if (isSj) {
      return this.props.sd.getSelectedSj()?.name;
    }
    if (isBle) {
      return this.props.sd.getSelectedBluetoothMethod()?.name;
    }
  };

  rename = (id: string): void => {
    this.setState({ isRenaming: true, renamingId: id }, () => {
      this.nameInput?.focus();
    });
  };

  handleNameKeypress = async (e: any, isWf: boolean, isSj: boolean, isApi: boolean, isBle: boolean) => {
    if (e.keyCode === 13) {
      this.handleNameOnBlur(e.currentTarget as HTMLInputElement, isWf, isSj, isApi, isBle);
    } else if (e.keyCode === 27) {
      this.setState({ isRenaming: false, renamingId: "" });
    }
  };

  // Enter and the tick glyph both hand over the <input> itself, so the new name is read off
  // the element instead of guessing between an event target and a component's internal state.
  handleNameOnBlur = async (input: HTMLInputElement | null, isWf: boolean, isSj: boolean, isApi: boolean, isBle) => {
    const newName: string = (input?.value ?? "").trim();
    if (isWf) {
      const wf = this.props.sd.selectedWf;
      this.changeWorkFlowName(wf, newName);
    } else if (isSj) {
      const sj = this.props.sd.getSelectedSj();
      this.changeScheduledJobName(sj, newName);
    } else if (isApi) {
      const api = this.props.sd.getSelectedApi();
      this.changeApiName(api, newName);
    } else if (isBle) {
      const bleMethod = this.props.sd.getSelectedBluetoothMethod();
      this.changeBleMethodName(bleMethod, newName);
    }
  };

  deleteItem = async (isWf: boolean, isSj: boolean, isApi: boolean, isBle) => {
    if (isWf) {
      this.deleteWorkFlow();
    } else if (isApi) {
      this.deleteApi();
    } else if (isSj) {
      this.deleteSj();
    } else if (isBle) {
      this.deleteBleMethod();
    }
  };

  render() {
    const wf = this.props.sd.selectedWf;
    const sj = this.props.sd.getSelectedSj();
    const api = this.props.sd.getSelectedApi();
    const ble = this.props.sd.getSelectedBluetoothMethod();
    const isWf = this.props.sd.getVisibility(ViewPart.WorkflowRightPanel) === Visibility.Visible;
    const isApi = this.props.sd.getVisibility(ViewPart.ApiManagementPropertiesPanel) === Visibility.Visible;
    const isSj = this.props.sd.getVisibility(ViewPart.ScheduledJobsPropertiesPanel) === Visibility.Visible;
    const isBle = this.props.sd.getVisibility(ViewPart.BluetoothPropertiesPanel) === Visibility.Visible;

    // this.props.sd.datasources.find(p => p.datasourceType === EDatasourceType.Mqtt)?.mqttConnectionInfo. // TODO

    const propertiesHeaderMenu: KuikaDropdownMenuItemType[] = [];
    if (isWf && wf) {
      propertiesHeaderMenu.push({ key: "wf-rename", label: "Rename", onClick: () => this.rename(wf.id) });
      if (wf.inactived !== true) {
        propertiesHeaderMenu.push({
          key: "wf-inactivate",
          label: "Inactivate",
          onClick: () => this.inactivateWorkFlow(wf.id)
        });
      } else {
        propertiesHeaderMenu.push({
          key: "wf-activate",
          label: "Activate",
          onClick: () => this.activateWorkFlow(wf.id)
        });
      }
      propertiesHeaderMenu.push({
        key: "wf-delete",
        label: "Delete",
        danger: true,
        onClick: () => this.triggerDeleteModal(wf)
      });
    }
    if (isSj && sj) {
      if (sj.json.isActive === true) {
        propertiesHeaderMenu.push({
          key: "sj-inactivate",
          label: "Inactivate",
          onClick: () => this.inactivateSj(sj.id)
        });
      } else {
        propertiesHeaderMenu.push({ key: "sj-activate", label: "Activate", onClick: () => this.activateSj(sj.id) });
      }
      propertiesHeaderMenu.push({
        key: "sj-delete",
        label: "Delete",
        danger: true,
        onClick: () => this.triggerDeleteModal(sj)
      });
    }
    if (isApi && api) {
      propertiesHeaderMenu.push({
        key: "api-delete",
        label: "Delete",
        danger: true,
        onClick: () => this.triggerDeleteModal(api)
      });
    }
    if (isBle && ble) {
      propertiesHeaderMenu.push({
        key: "ble-delete",
        label: "Delete",
        danger: true,
        onClick: () => this.triggerDeleteModal(ble)
      });
    }
    return (
      <>
        {this.state.itemToDelete && (
          <DeleteModal
            itemName={this.state.itemToDelete.name}
            triggerModal={this.state.itemToDelete !== null}
            deleteItem={() => this.deleteItem(isWf, isSj, isApi, isBle)}
            deleteType={this.state.itemToDelete.name}
            closeModal={() => this.setState({ itemToDelete: null })}
          />
        )}
        <div
          className={OVERFLOW_REVEAL_HOST_CLASS}
          style={{
            padding: "12px 10px 13px 10px",
            borderBottom: "1px solid var(--dark-2)",
            borderLeft: "1px solid var(--dark-2)"
          }}
        >
          {!this.state.isRenaming ? (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", gap: "6px", alignItems: "center", minWidth: 0 }}>
                {isWf && <WorkflowPassiveSvg />}
                {isApi && <ApiManagementPassiveSvg />}
                {isSj && <ScheduledJobsPassiveSvg />}
                {isBle && <BluetoothSvg fill="var(--dark-6)" />}
                <span
                  style={{
                    display: "block",
                    minWidth: 0,
                    overflow: "hidden",
                    fontSize: "11px",
                    fontWeight: 400,
                    letterSpacing: "0.35px",
                    color: "var(--dark-8)"
                  }}
                >
                  <KuikaOverflowText>{this.getItemName(isWf, isSj, isApi, isBle)}</KuikaOverflowText>
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  paddingTop: 2,
                  flexShrink: 0
                }}
              >
                <KuikaDropdownMenu options={propertiesHeaderMenu} trigger="click" placement="bottomRight">
                  <KuikaSvg name="dots" width={16} height={16} pointer />
                </KuikaDropdownMenu>
              </div>
            </div>
          ) : (
            <div
              onClick={(e) => e.stopPropagation()}
              className="kuika_renameScreen_input kuika_renameScreen_input__right-panel"
            >
              <KuikaInput
                defaultValue={this.getItemName(isWf, isSj, isApi, isBle)}
                onKeyDown={(e) => {
                  this.handleNameKeypress(e, isWf, isSj, isApi, isBle);
                }}
                ref={(input: HTMLInputElement | HTMLTextAreaElement | null) => {
                  this.nameInput = input as HTMLInputElement | null;
                }}
                disabled={this.state.isRenameLoading}
              />
              <KuikaSvg
                onClick={() => this.handleNameOnBlur(this.nameInput, isWf, isSj, isApi, isBle)}
                name="check"
                width={20}
                height={20}
                color="var(--blue-5)"
                pointer
              />
              <KuikaSvg
                onClick={() => this.setState({ isRenaming: false, renamingId: "" })}
                name="x"
                width={20}
                height={20}
                color="var(--dark-5)"
                pointer
              />
            </div>
          )}
        </div>
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const rightPanelItemNameComponent = withContext<IRightPanelItemNameComponentProps>(
  withSD<IRightPanelItemNameComponentProps>(RightPanelItemNameComponent)
);
export { rightPanelItemNameComponent as RightPanelItemNameComponent };
